'use client';

import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { cn } from '@skybeam/shared-utils/index';
import { Button } from '@simulmedia/ui-kit';
import { urlFor } from '@skybeam/shared-sanity';

import { Play } from 'lucide-react';
import { VideoModal, convertToEmbedUrl } from './video-modal';
import { utils } from '../utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../utils/types';

type TestimonialItem = {
  avatar: SanityImage;
  logo: SanityImage;
  name: string;
  text: string;
  thumbnail: SanityImage;
  title: string;
  video: string;
};

export type TestimonialSectionSanityProps = {
  backgroundColor?: string;
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  heading: string;
  partnerLogos: SanityImage[];
  testimonials: TestimonialItem[];
};

export const TestimonialSectionSanity = ({
  backgroundColor = 'white',
  containerPadding,
  contentMaxWidth,
  heading,
  partnerLogos,
  testimonials,
}: TestimonialSectionSanityProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const positionRef = useRef(0);

  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inlineVideoUrl, setInlineVideoUrl] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  const duplicatedLogos = [...partnerLogos, ...partnerLogos];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleVideoClick = (videoUrl: string) => {
    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 960) {
      setActiveVideoUrl(videoUrl);
      setIsModalOpen(true);
      setInlineVideoUrl(null);
    } else {
      setInlineVideoUrl(videoUrl);
      setActiveVideoUrl(null);
      setIsModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveVideoUrl(null);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 960 && inlineVideoUrl) {
        setInlineVideoUrl(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inlineVideoUrl]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const updateAnimation = () => {
      const totalWidth = slider.scrollWidth;
      const firstSetWidth = totalWidth / 2;

      const speed = firstSetWidth / (55 * 40);

      const animate = () => {
        positionRef.current -= speed;

        if (Math.abs(positionRef.current) >= firstSetWidth) {
          positionRef.current = 0;
        }

        slider.style.transform = `translateX(${positionRef.current}px)`;
        animationRef.current = requestAnimationFrame(animate);
      };

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      positionRef.current = 0;
      animationRef.current = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateAnimation();
    });

    resizeObserver.observe(slider);

    updateAnimation();

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );

  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  const firstTestimonial = testimonials[0];
  const thumbnailUrl =
    firstTestimonial?.thumbnail?.asset &&
    urlFor(firstTestimonial.thumbnail.asset).width(800).url();

  return (
    <>
      {styleTags}
      <div
        data-section="testimonial-section"
        className={cn(
          'tablet:py-20 desktop:py-24 flex w-full justify-center overflow-hidden px-4 py-14',
          containerPaddingStyle.className,
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            'tablet:max-w-[520px] desktop:gap-y-16 laptop:gap-y-19 laptop:max-w-[880px] tablet:gap-y-13 desktop:max-w-[1180px] flex w-full flex-col items-center gap-y-7',
            contentMaxWidthStyle.className,
          )}
        >
          <h2 className="heading-marketing-2 font-light">{heading}</h2>
          <div className="desktop:max-w-[944px] laptop:flex-row tablet:justify-between flex w-full max-w-full flex-col gap-4">
            <div className="desktop:min-w-[464px] laptop:min-w-[431px] relative aspect-272/364 size-full overflow-hidden rounded-2xl">
              {inlineVideoUrl && isMounted ? (
                <div className="relative size-full">
                  <iframe
                    src={convertToEmbedUrl(inlineVideoUrl)}
                    className="h-full w-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    title={`${firstTestimonial?.name} Testimonial`}
                  />
                </div>
              ) : (
                <div
                  onClick={() =>
                    firstTestimonial && handleVideoClick(firstTestimonial.video)
                  }
                  onKeyDown={(e) => {
                    if (
                      (e.key === 'Enter' || e.key === ' ') &&
                      firstTestimonial
                    ) {
                      e.preventDefault();
                      handleVideoClick(firstTestimonial.video);
                    }
                  }}
                  className="group relative block size-full cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-label={`Play ${firstTestimonial?.name} testimonial video`}
                >
                  <div className="relative size-full">
                    {thumbnailUrl && (
                      <Image
                        className="object-cover"
                        src={thumbnailUrl}
                        alt={
                          firstTestimonial?.thumbnail?.alt ||
                          `${firstTestimonial?.name} Testimonial`
                        }
                        fill
                      />
                    )}
                  </div>
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-20 rounded-md opacity-0 shadow-[inset_0_0_30px_rgba(0,0,0,0.5)] transition-opacity duration-300 group-hover:opacity-100"
                  />
                  <div className="tablet:justify-between tablet:p-8 absolute bottom-0 z-20 flex w-full items-center justify-center pb-6">
                    <div className="tablet:block hidden text-[18px] text-white">
                      <div className="font-bold">{firstTestimonial?.name}</div>
                      <div>{firstTestimonial?.title}</div>
                    </div>
                    <Button
                      className="rounded-3xl px-6"
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (firstTestimonial) {
                          handleVideoClick(firstTestimonial.video);
                        }
                      }}
                    >
                      <Play className="fill-white" />
                      Watch Full Story
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="desktop:min-w-[464px] laptop:min-w-[431px] desktop:py-7 desktop:px-8 laptop:pt-8.5 laptop:pb-5 laptop:px-8.5 tablet:px-10 tablet:pt-7.5 tablet:pb-5.5 flex flex-col rounded-2xl border border-blue-50 px-5.5 pt-6.5 pb-7.5 shadow-lg">
              {testimonials.map((testimonial, index) => {
                const avatarUrl =
                  testimonial.avatar?.asset &&
                  urlFor(testimonial.avatar.asset).width(100).url();
                const logoUrl =
                  testimonial.logo?.asset &&
                  urlFor(testimonial.logo.asset).width(200).url();

                const applySeparatorStyles = index % 2 === 0;

                return (
                  <div
                    key={testimonial.name}
                    className={cn(
                      'tablet:gap-y-9.5 laptop:gap-y-8 desktop:gap-y-8.5 flex flex-col gap-y-5',
                      applySeparatorStyles
                        ? 'desktop:pb-6 laptop:pb-3.5 tablet:pb-4 border-b border-blue-100 pb-8'
                        : 'desktop:pt-6 laptop:pt-3.5 tablet:pt-4 pt-8',
                    )}
                  >
                    <div className="tablet:gap-y-3 laptop:gap-y-2.5 flex flex-col gap-y-1.5">
                      {logoUrl && (
                        <div className="relative h-8 min-h-10 w-auto">
                          <Image
                            src={logoUrl}
                            alt={testimonial.logo?.alt || testimonial.name}
                            fill
                            className="object-contain object-left"
                          />
                        </div>
                      )}
                      <div className="tablet:text-lg laptop:text-base text-sm">
                        “{testimonial.text}”
                      </div>
                    </div>
                    <div className="laptop:gap-x-3 tablet:gap-x-3.5 flex items-center">
                      {avatarUrl && (
                        <div className="tablet:block relative hidden size-20 shrink-0">
                          <Image
                            src={avatarUrl}
                            alt={testimonial.avatar?.alt || testimonial.name}
                            fill
                            className="rounded-full object-cover"
                          />
                        </div>
                      )}
                      <div>
                        <div className="laptop:text-sm text-base font-bold">
                          {testimonial.name}
                        </div>
                        <div className="tablet:text-base laptop:text-sm text-sm">
                          {testimonial.title}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {partnerLogos && partnerLogos.length > 0 && (
            <div className="tablet:mt-8 desktop:mt-12.5 -mx-4 mt-5 w-[calc(100%+2rem)]">
              <div className="relative overflow-hidden">
                <div
                  ref={sliderRef}
                  className="tablet:gap-x-14 laptop:gap-x-18.5 inline-flex items-center gap-x-9 whitespace-nowrap will-change-transform"
                >
                  {duplicatedLogos.map((logo, index) => {
                    const logoUrl =
                      logo?.asset && urlFor(logo.asset).width(200).url();
                    if (!logoUrl) return null;

                    const dimensions = logo.metadata?.dimensions;
                    const width = dimensions?.width
                      ? Math.min(dimensions.width, 200)
                      : 200;
                    const height =
                      dimensions?.width && dimensions?.height
                        ? (dimensions.height / dimensions.width) * width
                        : 32;

                    return (
                      <div
                        key={index}
                        className="flex h-18 shrink-0 items-center"
                      >
                        <Image
                          src={logoUrl}
                          alt={logo.alt || `Partner logo ${index + 1}`}
                          width={width}
                          height={height}
                          className="w-auto object-contain"
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-linear-to-r from-white to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-linear-to-l from-white to-transparent" />
              </div>
            </div>
          )}
        </div>
        <VideoModal
          open={isModalOpen}
          videoUrl={activeVideoUrl}
          onClose={handleCloseModal}
        />
      </div>
    </>
  );
};
